import React, { useState, useEffect } from 'react';
import AuthLayout from './AuthLayout';
import { supabase } from '../../lib/supabase';
import { Lock, ArrowRight, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ResetPasswordPage = ({ isDarkMode, setIsDarkMode }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        // Double check if we landed here via a recovery link
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            // If there's no session and no hash in URL, someone just typed this URL manually
            if (!session && !window.location.hash) {
                console.warn("No recovery session found, redirecting to login");
                navigate('/login');
            }
        };
        checkSession();
    }, [navigate]);

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) throw error;
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <AuthLayout isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}>
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 text-green-500 mb-6">
                        <CheckCircle size={32} />
                    </div>
                    <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-tighter">Password Updated</h2>
                    <p className="text-slate-400 font-bold text-sm mb-8">
                        Your password has been successfully updated. Redirecting to login...
                    </p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}>
            <div className="text-center mb-8">
                <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-tighter">Set New Password</h2>
                <p className="text-slate-400 font-bold text-sm">
                    Enter your new secure password below to regain access.
                </p>
            </div>

            <form onSubmit={handleUpdatePassword} className="space-y-4">
                {error && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold">
                        {error}
                    </div>
                )}

                <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                        type="password"
                        placeholder="New Password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full pl-12 pr-4 py-4 rounded-2xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                            ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                            : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                            }`}
                    />
                </div>

                <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                        type="password"
                        placeholder="Confirm New Password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full pl-12 pr-4 py-4 rounded-2xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                            ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                            : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                            }`}
                    />
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-mystic-teal to-blue-600 text-white font-black text-sm uppercase tracking-widest hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-mystic-teal/20 disabled:opacity-50 flex items-center justify-center gap-2 group"
                >
                    {loading ? 'Updating...' : (
                        <>
                            Update Password
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </>
                    )}
                </button>
            </form>
        </AuthLayout>
    );
};

export default ResetPasswordPage;
